// src/components/PeoplePanel.tsx
import React, { useEffect, useState } from 'react';
import { TbX, TbCheck } from 'react-icons/tb';
import { PersonWithCount, getAvatarColor, getInitials } from '../utils/peopleUtils';
import './PeoplePanel.css';

interface PeoplePanelProps {
  people: PersonWithCount[];
  selectedPersonId: string | null;
  onPersonSelect: (personId: string | null) => void;
  onClose: () => void;
  isModal?: boolean;
  width?: number;
  onWidthChange?: (width: number) => void;
}

export const PeoplePanel: React.FC<PeoplePanelProps> = ({
  people,
  selectedPersonId,
  onPersonSelect,
  onClose,
  isModal = false,
  width = 240,
  onWidthChange,
}) => {
  const [isResizing, setIsResizing] = useState(false);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle resize
  useEffect(() => {
    if (!isResizing || isModal) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = e.clientX - 64; // 64px is sidebar width
      if (onWidthChange) {
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isModal, onWidthChange]);

  const handlePersonClick = (personId: string) => {
    // Toggle selection: if already selected, deselect; otherwise select
    if (selectedPersonId === personId) {
      onPersonSelect(null);
    } else {
      onPersonSelect(personId);
    }
  };

  const panelContent = (
    <div
      className={`people-panel ${isResizing ? 'resizing' : ''}`}
      style={!isModal ? { width: `${width}px` } : undefined}
    >
      {!isModal && (
        <div
          className="panel-resize-handle"
          onMouseDown={() => setIsResizing(true)}
        />
      )}
      <div className="people-panel-header">
        <span className="people-panel-title">People</span>
        <button
          className="people-panel-close"
          onClick={onClose}
          aria-label="Close people panel"
        >
          <TbX />
        </button>
      </div>
      <div className="people-list">
        {people.length === 0 ? (
          <div className="people-empty">
            No shared tasks yet.<br />
            Share a task to see collaborators here.
          </div>
        ) : (
          people.map((person) => {
            const isSelected = selectedPersonId === person.userId;
            return (
              <div
                key={person.userId}
                className={`person-row ${isSelected ? 'selected' : ''}`}
                onClick={() => handlePersonClick(person.userId)}
              >
                <div
                  className="person-avatar"
                  style={
                    person.photoURL
                      ? undefined
                      : { backgroundColor: getAvatarColor(person.email) }
                  }
                >
                  {person.photoURL ? (
                    <img src={person.photoURL} alt={person.email} />
                  ) : (
                    getInitials(person.email)
                  )}
                </div>
                <div className="person-info">
                  <div className="person-email">{person.email}</div>
                  <div className="person-task-count">
                    {person.taskCount} {person.taskCount === 1 ? 'task' : 'tasks'}
                  </div>
                </div>
                <div className="person-check">
                  <TbCheck />
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="people-footer">
        Click a person to filter tasks
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="people-modal" onClick={handleOverlayClick}>
        {panelContent}
      </div>
    );
  }

  return panelContent;
};
